import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sun, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Seo } from "@/components/Seo";
import { api } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const refCode = params.get("ref") || "";
  const [referrerName, setReferrerName] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!refCode) return;
    (async () => {
      try {
        const { data } = await api.get(`/referrals/resolve/${refCode}`);
        setReferrerName(data.referrer_name || "");
      } catch {}
    })();
  }, [refCode]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    const res = await register(name, email, password, refCode || null);
    setLoading(false);
    if (res.ok) {
      toast.success("Account created!");
      nav("/dashboard");
    } else {
      setErr(res.error);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <Seo
        title="Create your free account"
        description="Sign up for HomeSchool Hub — schedule lessons, track WRAT-5 scores, and generate compliant Florida evaluation packets in minutes."
        path="/register"
      />
      <div className="hidden md:block relative">
        <img src="https://images.unsplash.com/photo-1773332598413-a6d5279d1ae8" alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/70 via-amber-400/40 to-transparent" />
        <div className="relative p-12 text-white h-full flex flex-col justify-between">
          <Link to="/" className="flex items-center gap-2 font-display font-bold text-xl" data-testid="brand-link">
            <span className="inline-grid place-items-center w-9 h-9 rounded-2xl bg-white text-amber-500"><Sun className="w-5 h-5" /></span>
            HomeSchool Hub
          </Link>
          <div>
            <div className="font-display text-3xl font-semibold leading-tight max-w-md">
              Start planning smarter today.
            </div>
            <div className="mt-3 text-white/80">7-day free trial per student · No card required.</div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="mt-2 text-stone-500">Teachers only &mdash; you&apos;ll add students once inside.</p>

          {referrerName && (
            <div className="mt-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-900 px-4 py-3 text-sm flex items-center gap-2" data-testid="referral-banner">
              <Users className="w-4 h-4 text-emerald-600" />
              <span>You were invited by <strong>{referrerName}</strong> &mdash; both accounts get a bonus month of Premium.</span>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="register-form">
            <div>
              <Label htmlFor="name">Your name</Label>
              <Input id="name" required value={name} onChange={(e) => setName(e.target.value)} className="mt-2 rounded-xl h-12" data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 rounded-xl h-12" data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-2 rounded-xl h-12" data-testid="register-password-input" />
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="register-error">
                {err}
              </div>
            )}
            <Button type="submit" disabled={loading} className="w-full h-12 rounded-full bg-amber-500 hover:bg-amber-600 text-white text-base" data-testid="register-submit">
              {loading ? "Creating…" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-stone-500">
            Already have one?{" "}
            <Link to="/login" className="text-amber-600 hover:underline font-medium" data-testid="register-to-login">
              Sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
